import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ReverseApi } from "~/functions/api/reverse";
import type {
  DependencyAnalysis,
  RequirementsAnalysis,
  ReverseEngineeringReportDetail,
  SelectedApp,
} from "~/interface/reverse";
import type { RootState } from "~/store";

export type DocumentType = "requirements" | "dependency" | "erDiagram";

export interface ReverseEngineeringState {
  reportData: ReverseEngineeringReportDetail | null;
  copyReportData: ReverseEngineeringReportDetail | null;
  editableData: {
    requirements: any | null;
    dependency: any | null;
    erDiagram: any | null;
  };
  isEditable: {
    requirements: boolean;
    dependency: boolean;
    erDiagram: boolean;
  };
  isLoading: boolean;
  error: string | null;
  currentReportId: string | null;
  currentProjectId: string | null;
}

const initialState: ReverseEngineeringState = {
  reportData: null,
  copyReportData: null,
  editableData: {
    requirements: null,
    dependency: null,
    erDiagram: null,
  },
  isEditable: {
    requirements: false,
    dependency: false,
    erDiagram: false,
  },
  isLoading: false,
  error: null,
  currentReportId: null,
  currentProjectId: null,
};

const reverseApi = new ReverseApi();
// Fetch reverse engineering report
export const fetchReverseEngineeringReport = createAsyncThunk(
  "reverseEngineering/fetchReport",
  async (
    {
      organizationId,
      projectId,
      reportId,
    }: { organizationId: string; projectId: string; reportId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await reverseApi.getReverseEngineeringReport(
        organizationId,
        projectId,
        reportId,
      );
      if (response === null) {
        return rejectWithValue("レポートの取得に失敗しました");
      }
      return { reportData: response, reportId, projectId };
    } catch (_error) {
      return rejectWithValue("データの取得中にエラーが発生しました");
    }
  },
);

// Acquire lock for specific document type
export const acquireDocumentLock = createAsyncThunk(
  "reverseEngineering/acquireLock",
  async (
    { documentType, documentId }: { documentType: DocumentType; documentId: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const { currentUser } = state.auth;
      const { currentProjectId } = state.reverseEngineering;

      if (!currentProjectId) {
        return rejectWithValue("プロジェクトIDが見つかりません");
      }

      const success = await reverseApi.createProcessLock(
        currentUser.organization_id,
        currentProjectId,
        documentId,
        "edit",
      );

      if (!success) {
        return rejectWithValue("編集権限を取得できませんでした");
      }

      return { documentType, documentId, success: true };
    } catch (_error) {
      return rejectWithValue("ロックの取得中にエラーが発生しました");
    }
  },
);

// Release lock for specific document type
export const releaseDocumentLock = createAsyncThunk(
  "reverseEngineering/releaseLock",
  async (
    { documentType, documentId }: { documentType: DocumentType; documentId: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const { currentUser } = state.auth;
      const { currentProjectId } = state.reverseEngineering;

      if (!currentProjectId) {
        return rejectWithValue("プロジェクトIDが見つかりません");
      }

      await reverseApi.deleteProcessLock(
        currentUser.organization_id,
        currentProjectId,
        documentId,
        "edit",
      );

      return { documentType, documentId, success: true };
    } catch (_error) {
      return rejectWithValue("ロックの解放中にエラーが発生しました");
    }
  },
);

// Update specific document
export const updateReverseDocument = createAsyncThunk(
  "reverseEngineering/updateDocument",
  async (
    { documentType, documentId }: { documentType: DocumentType; documentId: string },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const { currentUser } = state.auth;
      const { reportData, currentProjectId } = state.reverseEngineering;

      if (!reportData || !currentProjectId) {
        return rejectWithValue("レポートデータが見つかりません");
      }

      let content: string;
      switch (documentType) {
        case "requirements":
          content = JSON.stringify(reportData.requirementsAnalysis);
          break;
        case "dependency":
          content = JSON.stringify(reportData.dependencyAnalysis);
          break;
        case "erDiagram":
          content = JSON.stringify(reportData.selectedApps);
          break;
        default:
          return rejectWithValue("不明なドキュメントタイプです");
      }

      const success = await reverseApi.updateReverseEngineeringDocument(
        currentUser.organization_id,
        currentProjectId,
        documentId,
        content,
      );

      // Release lock after update
      await dispatch(releaseDocumentLock({ documentType, documentId }));

      if (success) {
        return { documentType, documentId, success: true };
      } else {
        return rejectWithValue("保存に失敗しました");
      }
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

const reverseEngineeringSlice = createSlice({
  name: "reverseEngineering",
  initialState,
  reducers: {
    // Set report data
    setReportData: (state, action: PayloadAction<ReverseEngineeringReportDetail>) => {
      state.reportData = action.payload;
      state.copyReportData = JSON.parse(JSON.stringify(action.payload));
    },

    // Update requirements analysis
    updateRequirementsAnalysis: (state, action: PayloadAction<RequirementsAnalysis>) => {
      if (state.reportData) {
        state.reportData.requirementsAnalysis = action.payload;
      }
    },

    // Update dependency analysis
    updateDependencyAnalysis: (state, action: PayloadAction<DependencyAnalysis>) => {
      if (state.reportData) {
        state.reportData.dependencyAnalysis = action.payload;
      }
    },

    // Update selected apps (ER Diagram)
    updateSelectedApps: (state, action: PayloadAction<SelectedApp[]>) => {
      if (state.reportData) {
        state.reportData.selectedApps = action.payload;
      }
    },

    // Create copy for editing
    setCopyData: (state) => {
      if (state.reportData) {
        state.copyReportData = JSON.parse(JSON.stringify(state.reportData));
      }
    },

    // Restore from copy (cancel changes)
    restoreFromCopy: (state, action: PayloadAction<DocumentType>) => {
      if (state.copyReportData) {
        switch (action.payload) {
          case "requirements":
            if (state.reportData && state.copyReportData.requirementsAnalysis) {
              state.reportData.requirementsAnalysis = JSON.parse(
                JSON.stringify(state.copyReportData.requirementsAnalysis),
              );
            }
            break;
          case "dependency":
            if (state.reportData && state.copyReportData.dependencyAnalysis) {
              state.reportData.dependencyAnalysis = JSON.parse(
                JSON.stringify(state.copyReportData.dependencyAnalysis),
              );
            }
            break;
          case "erDiagram":
            if (state.reportData && state.copyReportData.selectedApps) {
              state.reportData.selectedApps = JSON.parse(
                JSON.stringify(state.copyReportData.selectedApps),
              );
            }
            break;
        }
      }
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Set current IDs
    setCurrentIds: (state, action: PayloadAction<{ reportId: string; projectId: string }>) => {
      state.currentReportId = action.payload.reportId;
      state.currentProjectId = action.payload.projectId;
    },

    // Set editable data for specific document type
    setEditableData: (state, action: PayloadAction<{ documentType: DocumentType; data: any }>) => {
      state.editableData[action.payload.documentType] = action.payload.data;
    },

    // Update editable data for specific document type
    updateEditableData: (
      state,
      action: PayloadAction<{ documentType: DocumentType; data: any }>,
    ) => {
      state.editableData[action.payload.documentType] = action.payload.data;
    },

    // Clear editable data for specific document type
    clearEditableData: (state, action: PayloadAction<DocumentType>) => {
      state.editableData[action.payload] = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch report
      .addCase(fetchReverseEngineeringReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReverseEngineeringReport.fulfilled, (state, action) => {
        state.reportData = action.payload.reportData;
        state.copyReportData = JSON.parse(JSON.stringify(action.payload.reportData));
        state.currentReportId = action.payload.reportId;
        state.currentProjectId = action.payload.projectId;
        state.isLoading = false;
      })
      .addCase(fetchReverseEngineeringReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Acquire lock
      .addCase(acquireDocumentLock.fulfilled, (state, action) => {
        const { documentType } = action.payload;
        state.isEditable[documentType] = true;
        state.error = null;

        // Create copy data when acquiring lock
        if (state.reportData) {
          state.copyReportData = JSON.parse(JSON.stringify(state.reportData));

          // Initialize editable data
          switch (documentType) {
            case "requirements":
              state.editableData.requirements = state.reportData.requirementsAnalysis
                ? JSON.parse(JSON.stringify(state.reportData.requirementsAnalysis))
                : null;
              break;
            case "dependency":
              state.editableData.dependency = state.reportData.dependencyAnalysis
                ? JSON.parse(JSON.stringify(state.reportData.dependencyAnalysis))
                : null;
              break;
            case "erDiagram":
              state.editableData.erDiagram = state.reportData.selectedApps
                ? JSON.parse(JSON.stringify(state.reportData.selectedApps))
                : null;
              break;
          }
        }
      })
      .addCase(acquireDocumentLock.rejected, (state, action) => {
        const payload = action.payload as string | undefined;
        state.error = payload ?? null;
        if (payload) {
          toast.error(payload);
        }
      })

      // Release lock
      .addCase(releaseDocumentLock.fulfilled, (state, action) => {
        const { documentType } = action.payload;
        state.isEditable[documentType] = false;
        // Clear editable data when releasing lock
        state.editableData[documentType] = null;
        state.error = null;
      })
      .addCase(releaseDocumentLock.rejected, (state, action) => {
        const payload = action.payload as string | undefined;
        state.error = payload ?? null;
        if (payload) {
          toast.error(payload);
        }
      })

      // Update document
      .addCase(updateReverseDocument.pending, (state) => {
        state.error = null;
      })
      .addCase(updateReverseDocument.fulfilled, (state, action) => {
        const { documentType } = action.payload;
        state.isEditable[documentType] = false;
        // Clear editable data after successful save
        state.editableData[documentType] = null;
        // Update copy data after successful save
        if (state.reportData) {
          state.copyReportData = JSON.parse(JSON.stringify(state.reportData));
        }
        toast.success(
          `${documentType === "requirements" ? "要件分析" : documentType === "dependency" ? "依存関係分析" : "ER図"}を保存しました`,
        );
      })
      .addCase(updateReverseDocument.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setReportData,
  updateRequirementsAnalysis,
  updateDependencyAnalysis,
  updateSelectedApps,
  setCopyData,
  restoreFromCopy,
  clearError,
  setCurrentIds,
  setEditableData,
  updateEditableData,
  clearEditableData,
} = reverseEngineeringSlice.actions;

export default reverseEngineeringSlice.reducer;
